import { Box } from '@mui/material'
import { CLEANING, KITCHEN, PRECLEAN } from './GenerateKitchenRows'
import { LINE } from './GenerateLineRows'
import { CHANGEOVER } from './GenerateChangeoverRows'
import { DOWN_TIME } from './CommonMethods'
import { GridCellParams } from '@mui/x-data-grid-premium'
import { memo } from 'react'
import { KITCHEN_COLORS, VIBRANT_KITCHEN_PALETTE } from './kitchenColors.config'

type RenderCellProps = {
  rowData: GridCellParams
  /** Accepted but unused. All four call sites pass `showNumber={true}`;
   *  the cell draws a bar, never a number. Kept so those call sites do not
   *  all have to change at once — remove the prop and its four usages
   *  together. */
  showNumber?: boolean
}

// Pre-calculated utility functions moved outside component to avoid recreation
/** Re-expresses an rgb(a) color string at a new alpha — shared by any view
 *  that needs to soften the (fully-saturated) kitchen palette below. */
export const adjustOpacity = (rgba: string, newAlpha: number): string => {
  const match = rgba?.match(/[\d.]+/g)
  const [r, g, b] = match ? match.map(Number) : [0, 0, 0, 1]
  return `rgba(${r}, ${g}, ${b}, ${newAlpha})`
}

const darkenRgb = (rgb: string, amount: number): string => {
  const match = rgb?.match(/\d+/g)
  const [r, g, b] = match ? match.map(Number) : [0, 0, 0]
  const factor = 1 - amount
  return `rgb(${Math.max(0, Math.min(255, r * factor))}, ${Math.max(0, Math.min(255, g * factor))}, ${Math.max(0, Math.min(255, b * factor))})`
}

const constructStyle = (rgbColor: string) => ({
  display: 'flex',
  backgroundColor: adjustOpacity(rgbColor, 0.4),
  border: `2px solid ${darkenRgb(rgbColor, 0.4)}`,
  borderRadius: '6px',
})

// Fallback rotation for a tenant/kitchen with no pinned entry in
// kitchenColors.config.ts — the same vibrant palette as the pinned tenants,
// so every tenant looks consistent by default.
const KITCHEN_PALETTE = VIBRANT_KITCHEN_PALETTE

// Cache raw colours per kitchen name — this is the source of truth for the legend
// and for any other view that needs a kitchen's base colour (e.g. the Factory
// schedule Gantt). Populated lazily as rows render, so the legend reflects only
// kitchens in the data.
const kitchenColorCache: Record<string, string> = {}
let nextColourIndex = 0

const resolveKitchenColor = (kitchenCode: string): string => {
  if (!kitchenColorCache[kitchenCode]) {
    // Prefer a pinned color from config; fall back to palette rotation
    const colour =
      KITCHEN_COLORS[kitchenCode] ??
      KITCHEN_PALETTE[nextColourIndex % KITCHEN_PALETTE.length]
    // Only advance the palette index when using a non-configured kitchen
    if (!KITCHEN_COLORS[kitchenCode]) {
      nextColourIndex++
    }
    kitchenColorCache[kitchenCode] = colour
  }
  return kitchenColorCache[kitchenCode]
}

const kitchenStyleCache: Record<string, ReturnType<typeof constructStyle>> = {}

export const getKitchenStyle = (kitchenCode: string) => {
  if (!kitchenStyleCache[kitchenCode]) {
    kitchenStyleCache[kitchenCode] = constructStyle(
      resolveKitchenColor(kitchenCode)
    )
  }
  return kitchenStyleCache[kitchenCode]
}

/** Returns kitchen name → style map used by the Legend component */
export const getKitchenStyleCache = () => kitchenStyleCache

/** Returns a kitchen's raw rgba colour — same source used by getKitchenStyle. */
export const getKitchenColor = (kitchenCode: string) =>
  resolveKitchenColor(kitchenCode)

/** Returns kitchen name → raw rgba colour map, e.g. for the Factory schedule legend. */
export const getKitchenColorCache = () => kitchenColorCache

// Pre-calculated cleaning style (post-clean - gray with stripes)
const CLEANING_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  backgroundColor: adjustOpacity('rgba(150, 150, 150, 0.49)', 0.4),
  border: `2px solid ${darkenRgb('rgba(150, 150, 150, 0.49)', 0.4)}`,
  borderRadius: '6px',
  backgroundImage: `linear-gradient(45deg, rgba(255,255,255,0.5) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.5) 75%, transparent 75%, transparent)`,
  backgroundSize: '20px 20px',
} as const

// Pre-calculated preclean style (preclean - purple/violet with stripes to distinguish from post-clean)
const PRECLEAN_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  backgroundColor: adjustOpacity('rgba(138, 43, 226, 0.6)', 0.4),
  border: `2px solid ${darkenRgb('rgba(138, 43, 226, 0.6)', 0.4)}`,
  borderRadius: '6px',
  backgroundImage: `linear-gradient(45deg, rgba(255,255,255,0.5) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.5) 75%, transparent 75%, transparent)`,
  backgroundSize: '20px 20px',
} as const

// Pre-calculated changeover style (orange/amber with stripes — distinct from grey cleaning)
const CHANGEOVER_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  backgroundColor: adjustOpacity('rgba(255, 140, 0, 0.6)', 0.4),
  border: `2px solid ${darkenRgb('rgba(255, 140, 0, 0.6)', 0.4)}`,
  borderRadius: '6px',
  backgroundImage: `linear-gradient(45deg, rgba(255,255,255,0.5) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.5) 75%, transparent 75%, transparent)`,
  backgroundSize: '20px 20px',
} as const

// Pre-calculated downtime style
const DOWNTIME_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  backgroundColor: '#8b7070',
  border: `2px solid ${darkenRgb('rgba(150, 150, 150, 0.49)', 0.4)}`,
  borderRadius: '6px',
  backgroundImage: `linear-gradient(45deg, rgba(255,255,255,0.5) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.5) 75%, transparent 75%, transparent)`,
  backgroundSize: '20px 20px',
} as const

// Pre-calculated hoverable cell style
const HOVERABLE_CELL_STYLE = {
  height: '100%',
  width: '100%',
  border: '1px dashed transparent',
  backgroundColor: 'transparent',
  '&:hover': {
    border: '1px dashed blue',
    backgroundColor: 'rgba(173, 216, 230, 0.3)',
  },
} as const

// Pre-calculated base active cell style
const BASE_ACTIVE_CELL_STYLE = {
  border: 'none',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0)',
  },
  '&:active': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
  borderRadius: '5px',
} as const

// Pre-calculated multiple processes overlay
const MULTIPLE_PROCESSES_OVERLAY = {
  backgroundImage: `linear-gradient(45deg, rgba(255, 255, 255, 0.5) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.5) 50%, rgba(255, 255, 255, 0.5) 75%, transparent 75%, transparent)`,
} as const

// Helper to get a segment identity key for a given hour cell.
// For production slots (KITCHEN/LINE), includes base_material_description
// so that borders break when the material changes.
const getCellSegmentKey = (row: any, field: string): string | null => {
  const cell = row[field]
  if (!cell || !cell.slot_types || cell.value === 0) return null
  const slotType = cell.slot_types[0]
  if (slotType === KITCHEN || slotType === LINE) {
    const material = cell.base_material_description?.[0] ?? ''
    return `${slotType}:${material}`
  }
  return slotType
}

export const RenderCell = memo(({ rowData }: RenderCellProps) => {
  // **No hooks in this component, deliberately.** The empty-cell early
  // return below used to sit between `useTheme()` and two `useMemo`s, so a
  // cell going from empty to occupied changed the hook count from 1 to 3
  // and React would throw "Rendered more hooks than during the previous
  // render". It had not fired yet — these cells remount rather than update
  // — but it was one data-shape change away. Keeping the body hook-free
  // makes the early return unconditionally safe.

  // Early return for empty cells to avoid unnecessary computations
  const value = Number.parseInt(rowData.formattedValue as string)
  if (value === 0) {
    return <Box sx={HOVERABLE_CELL_STYLE} />
  }

  // Extract data once
  const { row, field } = rowData
  const cell = row[field]
  const [slot_type] = cell?.slot_types ?? [KITCHEN]

  // Check adjacent cells to merge continuous segments (breaks on material change)
  const hourMatch = field.match(/^hour-(\d+)$/)
  const hour = hourMatch ? parseInt(hourMatch[1], 10) : -1
  const currentKey = getCellSegmentKey(row, field)
  const sameAsPrev =
    hour > 0 && getCellSegmentKey(row, `hour-${hour - 1}`) === currentKey
  const sameAsNext =
    hour < 167 && getCellSegmentKey(row, `hour-${hour + 1}`) === currentKey

  const continuityOverrides: Record<string, any> = {}
  if (sameAsPrev) {
    continuityOverrides.borderLeft = 'none'
    continuityOverrides.borderTopLeftRadius = 0
    continuityOverrides.borderBottomLeftRadius = 0
  } else {
    continuityOverrides.marginLeft = '1px'
  }
  if (sameAsNext) {
    continuityOverrides.borderRight = 'none'
    continuityOverrides.borderTopRightRadius = 0
    continuityOverrides.borderBottomRightRadius = 0
  } else {
    continuityOverrides.marginRight = '1px'
  }

  // Align striped background patterns across adjacent cells (columns are 50px wide)
  if (hour >= 0) {
    continuityOverrides.backgroundPosition = `${-(hour * 50)}px 0`
  }

  // Determine cell styles based on slot type
  let cellStyles
  if (slot_type === KITCHEN || slot_type === LINE) {
    // Colour by kitchen so the legend stays consistent with the combined view.
    // row.kitchen is set directly on kitchen-view rows.
    // For line-view rows, fall back to the kitchens[] array on the cell.
    const kitchenCode = row.kitchen ?? cell?.kitchens?.[0] ?? 'unknown'
    cellStyles = getKitchenStyle(kitchenCode)
  } else if (slot_type === CLEANING || slot_type === PRECLEAN) {
    // All cleaning (post-clean and pre-clean) uses the same grey style
    cellStyles = CLEANING_STYLE
  } else if (slot_type === CHANGEOVER) {
    cellStyles = CHANGEOVER_STYLE
  } else if (slot_type === DOWN_TIME) {
    cellStyles = DOWNTIME_STYLE
  } else {
    cellStyles = getKitchenStyle('unknown')
  }

  // Built plainly rather than memoized. The component is already `memo`'d,
  // so it only renders when its props change; `getKitchenStyle` is cached,
  // so `cellStyles` is a stable constant. Four spreads of small objects
  // cost less than a five-element dependency array plus the closure a
  // `useMemo` allocates — and it is a hook, which is what made the early
  // return above unsafe.
  const finalStyles = {
    ...BASE_ACTIVE_CELL_STYLE,
    ...cellStyles,
    ...(value > 1 ? MULTIPLE_PROCESSES_OVERLAY : {}),
    ...continuityOverrides,
  }

  return <Box sx={finalStyles} />
})
